import { useIntl } from 'react-intl';

import type { Notification } from '@hooks/notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, ListItemButton, Stack, Typography } from '@mui/material';

import { getRelativeTime, isUnread } from './notifications.utils.js';

interface NotificationRowProps {
  notification: Notification;
  now: number;
  onClick: (notification: Notification) => void;
}

export function NotificationRow({ notification, now, onClick }: NotificationRowProps) {
  const intl = useIntl();
  const unread = isUnread(notification);
  const relativeTime = getRelativeTime(notification.createdAt, now);
  const isFailure = notification.outcome === 'failure';

  return (
    <ListItemButton
      alignItems="flex-start"
      data-testid="notification-row"
      data-unread={unread}
      sx={{
        gap: 1.5,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: unread ? 'primary.lighter' : 'transparent',
      }}
      onClick={() => onClick(notification)}
    >
      {isFailure ? (
        <ErrorOutlineIcon color="error" data-testid="notification-outcome-failure" />
      ) : (
        <CheckCircleIcon color="success" data-testid="notification-outcome-success" />
      )}
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={unread ? 'bold' : 'medium'} variant="subtitle2">
          {notification.title}
        </Typography>
        <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }} variant="body2">
          {notification.message}
        </Typography>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
          {notification.resourceLabel && (
            <Typography color="primary" fontWeight="medium" variant="caption">
              {notification.resourceLabel}
            </Typography>
          )}
          {relativeTime && (
            <Typography color="text.disabled" variant="caption">
              {intl.formatRelativeTime(relativeTime.value, relativeTime.unit, {
                numeric: 'auto',
                style: 'short',
              })}
            </Typography>
          )}
        </Stack>
      </Stack>
      {unread && (
        <Box
          data-testid="notification-unread-dot"
          sx={{
            width: 8,
            height: 8,
            mt: 0.75,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: 'info.main',
          }}
        />
      )}
    </ListItemButton>
  );
}
